import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Card, CardMedia, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

const SwipeCard = ({ product, onLike, onDislike, onZoomImage }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [initialSwing, setInitialSwing] = useState(true); // Track the initial swing
  const [imageIndex, setImageIndex] = useState(0);

  const animationFrameRef = useRef(null); // Store the animation frame reference
  
  const handleZoomOpen = () => onZoomImage(imageIndex);
  const handleTouchStart = (event) => {
    setIsDragging(true);
    setStartPosition({ x: event.touches[0].clientX, y: event.touches[0].clientY });
  };

  const handleTouchMove = (event) => {
    if (!isDragging) return;

    // Prevent page scrolling
    event.preventDefault();

    const xDiff = event.touches[0].clientX - startPosition.x;
    const yDiff = event.touches[0].clientY - startPosition.y;
    setPosition({ x: xDiff, y: yDiff });
  };

  const handleTouchEnd = (event) => {
    setIsDragging(false);

    const xDiff = event.changedTouches[0].clientX - startPosition.x;

    if (xDiff > 150) {
      onLike();
    } else if (xDiff < -150) {
      onDislike();
    }
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (event) => {
    setIsDragging(true);
    setStartPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;

    const xDiff = event.clientX - startPosition.x;
    const yDiff = event.clientY - startPosition.y;
    setPosition({ x: xDiff, y: yDiff });
  };

  const handleMouseUp = (event) => {
    setIsDragging(false);

    const xDiff = event.clientX - startPosition.x;

    if (xDiff > 150) {
      onLike();
    } else if (xDiff < -150) {
      onDislike();
    }
    setPosition({ x: 0, y: 0 });
  };
  
  const handleImageClick = (index) => {
    setImageIndex(index);
  };

  const handleBuy = (product) => {
    window.location.href = "/?add-to-cart=" + product.id;
  };

  const rotation = position.x * 0.1;

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('touchend', handleTouchEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      return () => {
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isDragging]);

  // Use requestAnimationFrame for smooth swing effect
  useEffect(() => {
    if (initialSwing) {
      let start = null;
      const swingDuration = 1000; // Swing duration in ms
      let startX = 20; // Starting position for the swing

      const animateSwing = (timestamp) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const swingProgress = Math.min(progress / swingDuration, 1);
        const x = startX * (1 - swingProgress) * Math.sin(swingProgress * Math.PI);

        setPosition({ x: x, y: 0 });

        if (swingProgress < 1) {
          animationFrameRef.current = requestAnimationFrame(animateSwing);
        } else {
          setPosition({ x: 0, y: 0 });
          setInitialSwing(false);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animateSwing);

      return () => {
        cancelAnimationFrame(animationFrameRef.current);
      };
    }
  }, [initialSwing]);

  const likeOpacity = position.x > 50 ? Math.min(position.x / 150, 1) : 0;
  const dislikeOpacity = position.x < -50 ? Math.min(-position.x / 150, 1) : 0;

  return (
    <Box position="relative" width="100%" margin="0 auto" maxHeight="90vh">
      <div
        className="swipe-card"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          transform: `translate(${position.x}px, ${0}px) rotate(${rotation}deg)`,
          transformOrigin: 'bottom center',
          transition: isDragging || initialSwing ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
          zIndex: 1,
        }}
      >
        <Card style={{ userSelect: 'none' }}>
          <Box position="relative">
            <CardMedia
              image={product.imageUrls[imageIndex]}
              component="img"
              sx={{
                userSelect: 'none',
                pointerEvents: 'none',
                objectFit: 'contain',
                width: 'auto',
                height: 'auto',
                maxHeight: '80vh',
                maxWidth: '80vw',
              }}
            />
            <IconButton
              onClick={handleZoomOpen}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.5)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <ZoomInIcon />
            </IconButton>

          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            py={2}
            px={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: 'linear-gradient(0deg, rgba(107, 165, 44, 1), rgba(107, 165, 44, 0))',
              color: '#fff',
            }}
          >
            <Typography variant="body1">{product.name}</Typography>
            <Typography variant="h4">{product.price} Ft</Typography>
          </Box>
          </Box>
          <Box
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{
              zIndex: 2,
              opacity: likeOpacity,
              pointerEvents: 'none',
            }}
          >
            <FavoriteIcon
              style={{
                fontSize: 240,
                color: 'green',
                opacity: likeOpacity,
                transition: 'opacity 0.2s ease',
              }}
            />
          </Box>
          <Box
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{
              zIndex: 2,
              opacity: dislikeOpacity,
              pointerEvents: 'none',
            }}
          >
            <CloseIcon
              style={{
                fontSize: 240,
                color: 'red',
                opacity: dislikeOpacity,
                transition: 'opacity 0.2s ease',
              }}
            />
          </Box>
          <Box display="flex" justifyContent="center">
            {
              product.imageUrls.map((url, index) => (
                <Box padding={2} key={index}>
                  <img src={url} alt={product.name} style={{ maxwidth: '100px', maxHeight: '100px' }} onClick={() => handleImageClick(index)} />
                </Box>
              ))
            }
          </Box>
          <Button onClick={() => handleBuy(product)} variant="contained" fullWidth>
            Megrendelem
          </Button>
        </Card>
      </div>
    </Box>
  );
};

export default SwipeCard;
